import { useState } from 'react';
import { CheckCircle2, DollarSign, Plus, Wallet } from 'lucide-react';
import type { NuevoPagoDraft, Pago } from '../../types';
import { usePagos } from '../../hooks/usePagos';
import { nuevoUUID } from '../../lib/uuid';
import { FormularioPago } from './FormularioPago';
import { TarjetaPago } from './TarjetaPago';
import { DialogoConfirmacion } from '../ui/DialogoConfirmacion';

function hoyISO(): string {
  const ahora = new Date();
  const y = ahora.getFullYear();
  const m = String(ahora.getMonth() + 1).padStart(2, '0');
  const d = String(ahora.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function SeccionPagos() {
  const { pagos, guardarPago, marcarRealizado } = usePagos();

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editando, setEditando] = useState<Pago | null>(null);
  const [pagoARealizar, setPagoARealizar] = useState<Pago | null>(null);

  const abrirNuevo = () => {
    setEditando(null);
    setMostrarFormulario(true);
  };

  const cerrarFormulario = () => {
    setMostrarFormulario(false);
    setEditando(null);
  };

  const onGuardar = (draft: NuevoPagoDraft) => {
    const ahora = new Date().toISOString();
    const pago: Pago = {
      id: editando?.id ?? nuevoUUID(),
      destinatario: draft.destinatario,
      monto: draft.monto,
      fechaLimite: draft.fechaLimite,
      creado: editando?.creado ?? ahora,
      updatedAt: ahora,
      deletedAt: null,
    };
    void guardarPago(pago, editando === null);
    cerrarFormulario();
  };

  const confirmarRealizado = () => {
    if (pagoARealizar) void marcarRealizado(pagoARealizar.id);
    setPagoARealizar(null);
  };

  const hoy = hoyISO();
  const total = pagos.reduce((sum, p) => sum + p.monto, 0);

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <Wallet className="text-emerald-600" size={32} />
              Pagos Pendientes
            </h1>
            <p className="text-gray-600 mt-1">
              {pagos.length} pendientes •{' '}
              {total.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })} en total
            </p>
          </div>
          <button
            onClick={abrirNuevo}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-md"
          >
            <Plus size={20} />
            Nuevo Pago
          </button>
        </div>
      </div>

      {mostrarFormulario && (
        <FormularioPago
          key={editando?.id ?? 'nuevo'}
          editando={editando}
          onGuardar={onGuardar}
          onCancelar={cerrarFormulario}
        />
      )}

      {pagoARealizar && (
        <DialogoConfirmacion
          icono={<CheckCircle2 size={24} />}
          titulo="Marcar como realizado"
          mensaje={`¿Ya realizaste el pago a ${pagoARealizar.destinatario}? Se borrará de la lista.`}
          textoConfirmar="Sí, ya pagué"
          iconoBotonConfirmar={<CheckCircle2 size={18} />}
          variante="advertencia"
          onConfirmar={confirmarRealizado}
          onCancelar={() => setPagoARealizar(null)}
        />
      )}

      {pagos.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-500 flex flex-col items-center gap-2">
          <DollarSign size={32} className="text-gray-300" />
          No tienes pagos pendientes
        </div>
      ) : (
        <div className="space-y-4">
          {pagos.map((pago) => (
            <TarjetaPago
              key={pago.id}
              pago={pago}
              vencido={pago.fechaLimite < hoy}
              onEditar={(p) => {
                setEditando(p);
                setMostrarFormulario(true);
              }}
              onMarcarRealizado={(p) => setPagoARealizar(p)}
            />
          ))}
        </div>
      )}
    </>
  );
}
